import React, { useState, useEffect } from 'react';
import { 
  Search, Filter, Download, Eye, Edit, Trash2, Plus, 
  Calendar, User, Phone, Mail, DollarSign, Building2,
  ChevronDown, ArrowUpDown, MoreHorizontal, CheckCircle,
  XCircle, Clock, AlertCircle, FileText, CreditCard
} from 'lucide-react';

interface Application {
  id: string;
  type: 'loan' | 'card' | 'deposit' | 'remote_customer';
  customerName: string;
  phone: string;
  email?: string;
  amount?: string;
  bank?: string;
  status: 'pending' | 'processing' | 'approved' | 'rejected' | 'completed';
  createdAt: string;
  updatedAt: string;
  notes?: string;
}

const Applications = () => {
  const [applications, setApplications] = useState<Application[]>([
    {
      id: '1',
      type: 'loan',
      customerName: 'Ahmet Yılmaz',
      phone: '0532 123 45 67',
      email: 'ahmet@email.com',
      amount: '50.000 TL',
      bank: 'Akbank',
      status: 'pending',
      createdAt: '2024-01-18T10:30:00Z',
      updatedAt: '2024-01-18T10:30:00Z'
    },
    {
      id: '2',
      type: 'card',
      customerName: 'Ayşe Demir',
      phone: '0533 987 65 43',
      email: 'ayse@email.com',
      bank: 'Garanti BBVA',
      status: 'approved',
      createdAt: '2024-01-18T09:15:00Z',
      updatedAt: '2024-01-18T11:20:00Z'
    },
    {
      id: '3',
      type: 'deposit',
      customerName: 'Mehmet Kaya',
      phone: '0534 555 44 33',
      amount: '100.000 TL',
      bank: 'QNB',
      status: 'processing',
      createdAt: '2024-01-18T08:45:00Z',
      updatedAt: '2024-01-18T09:30:00Z'
    }
  ]);

  const [filters, setFilters] = useState({
    type: '',
    status: '',
    bank: '',
    dateRange: ''
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedApplications, setSelectedApplications] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'loan': return <DollarSign size={16} className="text-green-600" />;
      case 'card': return <CreditCard size={16} className="text-purple-600" />;
      case 'deposit': return <Building2 size={16} className="text-blue-600" />;
      case 'remote_customer': return <User size={16} className="text-orange-600" />;
      default: return <FileText size={16} className="text-slate-600" />;
    }
  };

  const getTypeText = (type: string) => {
    switch (type) {
      case 'loan': return 'Kredi';
      case 'card': return 'Kredi Kartı';
      case 'deposit': return 'Mevduat';
      case 'remote_customer': return 'Müşteri Edinimi';
      default: return 'Bilinmeyen';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved': return <CheckCircle size={16} className="text-green-600" />;
      case 'rejected': return <XCircle size={16} className="text-red-600" />;
      case 'processing': return <Clock size={16} className="text-blue-600" />;
      case 'completed': return <CheckCircle size={16} className="text-green-600" />;
      default: return <AlertCircle size={16} className="text-yellow-600" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'approved': return 'Onaylandı';
      case 'rejected': return 'Reddedildi';
      case 'processing': return 'İşleniyor';
      case 'completed': return 'Tamamlandı';
      default: return 'Beklemede';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'text-green-700 bg-green-100';
      case 'rejected': return 'text-red-700 bg-red-100';
      case 'processing': return 'text-blue-700 bg-blue-100';
      case 'completed': return 'text-green-700 bg-green-100';
      default: return 'text-yellow-700 bg-yellow-100';
    }
  };

  const handleStatusChange = (applicationId: string, newStatus: string) => {
    setApplications(prev => 
      prev.map(app => 
        app.id === applicationId 
          ? { ...app, status: newStatus as any, updatedAt: new Date().toISOString() }
          : app
      )
    );
  };

  const handleBulkAction = (action: string) => {
    if (selectedApplications.length === 0) return;
    
    switch (action) {
      case 'approve':
        setApplications(prev => 
          prev.map(app => 
            selectedApplications.includes(app.id)
              ? { ...app, status: 'approved', updatedAt: new Date().toISOString() }
              : app
          )
        );
        break;
      case 'reject':
        setApplications(prev => 
          prev.map(app => 
            selectedApplications.includes(app.id)
              ? { ...app, status: 'rejected', updatedAt: new Date().toISOString() }
              : app
          )
        );
        break;
      case 'delete':
        setApplications(prev => 
          prev.filter(app => !selectedApplications.includes(app.id))
        );
        break;
    }
    setSelectedApplications([]);
  };

  const filteredApplications = applications.filter(app => {
    const matchesSearch = app.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         app.phone.includes(searchTerm) ||
                         (app.email && app.email.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesType = !filters.type || app.type === filters.type;
    const matchesStatus = !filters.status || app.status === filters.status;
    const matchesBank = !filters.bank || app.bank === filters.bank;
    
    return matchesSearch && matchesType && matchesStatus && matchesBank;
  });

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Başvuru Yönetimi</h1>
              <p className="text-slate-600">Tüm başvuruları görüntüleyin ve yönetin</p>
            </div>
            <div className="flex items-center gap-3">
              <button className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors">
                <Download size={20} />
                <span>Dışa Aktar</span>
              </button>
              <button className="flex items-center gap-2 px-4 py-2 bg-[#F7BA45] text-slate-900 rounded-lg hover:bg-[#e5ac3d] transition-colors">
                <Plus size={20} />
                <span>Yeni Başvuru</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Filters and Search */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
              <input
                type="text"
                placeholder="Müşteri ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
              />
            </div>

            <select
              value={filters.type}
              onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
              className="px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
            >
              <option value="">Tüm Türler</option>
              <option value="loan">Kredi</option>
              <option value="card">Kredi Kartı</option>
              <option value="deposit">Mevduat</option>
              <option value="remote_customer">Müşteri Edinimi</option>
            </select>

            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              className="px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
            >
              <option value="">Tüm Durumlar</option>
              <option value="pending">Beklemede</option>
              <option value="processing">İşleniyor</option>
              <option value="approved">Onaylandı</option>
              <option value="rejected">Reddedildi</option>
              <option value="completed">Tamamlandı</option>
            </select>

            <select
              value={filters.bank}
              onChange={(e) => setFilters(prev => ({ ...prev, bank: e.target.value }))}
              className="px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
            >
              <option value="">Tüm Bankalar</option>
              <option value="Akbank">Akbank</option>
              <option value="Garanti BBVA">Garanti BBVA</option>
              <option value="QNB">QNB</option>
              <option value="Yapı Kredi">Yapı Kredi</option>
              <option value="İş Bankası">İş Bankası</option>
            </select>

            <input
              type="date"
              value={filters.dateRange}
              onChange={(e) => setFilters(prev => ({ ...prev, dateRange: e.target.value }))}
              className="px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
            />
          </div>
        </div>

        {/* Bulk Actions */}
        {selectedApplications.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600">
                {selectedApplications.length} başvuru seçildi
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleBulkAction('approve')}
                  className="px-3 py-1 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors text-sm"
                >
                  Onayla
                </button>
                <button
                  onClick={() => handleBulkAction('reject')}
                  className="px-3 py-1 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors text-sm"
                >
                  Reddet
                </button>
                <button
                  onClick={() => handleBulkAction('delete')}
                  className="px-3 py-1 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors text-sm"
                >
                  Sil
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Applications Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedApplications.length === filteredApplications.length}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedApplications(filteredApplications.map(app => app.id));
                        } else {
                          setSelectedApplications([]);
                        }
                      }}
                      className="rounded border-slate-300 text-[#F7BA45] focus:ring-[#F7BA45]"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Tür
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Müşteri
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    İletişim
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Tutar
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Banka
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Durum
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Tarih
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    İşlemler
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {filteredApplications.map((application) => (
                  <tr key={application.id} className="hover:bg-slate-50">
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedApplications.includes(application.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedApplications(prev => [...prev, application.id]);
                          } else {
                            setSelectedApplications(prev => prev.filter(id => id !== application.id));
                          }
                        }}
                        className="rounded border-slate-300 text-[#F7BA45] focus:ring-[#F7BA45]"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {getTypeIcon(application.type)}
                        <span className="text-sm font-medium text-slate-900">
                          {getTypeText(application.type)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-slate-900">{application.customerName}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-slate-900">{application.phone}</div>
                      {application.email && (
                        <div className="text-sm text-slate-500">{application.email}</div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-slate-900">{application.amount || '-'}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-slate-900">{application.bank || '-'}</div>
                    </td>
                    <td className="px-6 py-4">
                      <select
                        value={application.status}
                        onChange={(e) => handleStatusChange(application.id, e.target.value)}
                        className={`text-xs font-medium px-2 py-1 rounded-full border-0 ${getStatusColor(application.status)}`}
                      >
                        <option value="pending">Beklemede</option>
                        <option value="processing">İşleniyor</option>
                        <option value="approved">Onaylandı</option>
                        <option value="rejected">Reddedildi</option>
                        <option value="completed">Tamamlandı</option>
                      </select>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-slate-900">
                        {new Date(application.createdAt).toLocaleDateString('tr-TR')}
                      </div>
                      <div className="text-xs text-slate-500">
                        {new Date(application.createdAt).toLocaleTimeString('tr-TR')}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button className="p-1 text-slate-400 hover:text-slate-600">
                          <Eye size={16} />
                        </button>
                        <button className="p-1 text-slate-400 hover:text-slate-600">
                          <Edit size={16} />
                        </button>
                        <button className="p-1 text-slate-400 hover:text-red-600">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between mt-6">
          <div className="text-sm text-slate-600">
            {filteredApplications.length} başvuru gösteriliyor
          </div>
          <div className="flex items-center gap-2">
            <button className="px-3 py-1 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50">
              Önceki
            </button>
            <button className="px-3 py-1 bg-[#F7BA45] text-slate-900 rounded-lg text-sm">
              1
            </button>
            <button className="px-3 py-1 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50">
              2
            </button>
            <button className="px-3 py-1 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50">
              Sonraki
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Applications;